import React from "react";
import PageFrame from "./PageFrame";
import {formatPhoneNumber} from "../../../utils";

/**
 * Page 02, single tenant - Offering Summary + Lease Summary on the left, the property
 * photo and the Annualized Operating Data schedule on the right.
 *
 * This is where the single-tenant deck diverges most from the multi-tenant one. There:
 * offering summary over a locator map on the left, Income & Expenses down the right.
 * Here the map is gone (the photo takes the top of the right column) and the two cards
 * that replace it are the lease terms and the rent schedule.
 *
 * Card boxes, taken from the PDF and scaled by 4/3 like the rest of the template:
 *   offering summary  x 48   y 114  480 x 256
 *   lease summary     x 48   y 383  480 x 385
 *   property photo    x 546  y 113  463 x 205
 *   operating data    x 546  y 319  464 x 449
 */

/**
 * The schedule rows share the card between them, so a ten-year lease prints at the
 * template's own 32px pitch and a longer one compresses. Past this many the rows would be
 * too shallow for their own text, so the remainder is dropped - and counted in a note
 * rather than silently cut.
 */
const SCHEDULE_ROWS_MAX = 13;

export default function OfferingSummarySingle(props) {
    const {
        property,
        subProduct,
        offeringSummary,
        leaseSummary,
        annualizedOperatingData,
        propertyImageUrl,
        agents,
        pageNo,
        totalPages,
    } = props;

    const headerRight = [property.address, property.city, property.state, property.zip]
        .filter(Boolean)
        .join(', ');

    return (
        <PageFrame
            eyebrow={`OFFERING SUMMARY  •  ${subProduct}`}
            title={firstWord(property.name)}
            titleAccent={restOfName(property.name)}
            headerRight={headerRight}
            pageNo={pageNo}
            totalPages={totalPages}
        >
            <div className='bov-two-col bov-two-col--single'>
                <div className='bov-two-col__left'>
                    <KeyValueCard table={offeringSummary} modifier='summary'/>
                    <KeyValueCard table={leaseSummary} modifier='lease'/>
                    <AgentLine agents={agents} index={0}/>
                </div>

                <div className='bov-two-col__right'>
                    <div className='bov-photo'>
                        {propertyImageUrl
                            ? <img className='bov-photo__img' src={propertyImageUrl} alt=''/>
                            : null}
                    </div>

                    <OperatingData table={annualizedOperatingData}/>

                    <AgentLine agents={agents} index={1}/>
                </div>
            </div>
        </PageFrame>
    );
}

/**
 * Offering Summary and Lease Summary are the same label/value card, differing only in how
 * many rows they hold. Both gain rows from the underwriting - the offering summary gains
 * Clear Height / Doors / Office to Warehouse on industrial deals, the lease summary gains
 * Options to Purchase and Guarantor - and the rows divide the card between them, so no
 * row count is a special case here (see .bov-two-col--single in the stylesheet).
 */
function KeyValueCard({table, modifier}) {
    const rows = table.rows || [];

    return (
        <div className={`bov-card bov-card--${modifier}`}>
            <div className='bov-card__title'>{table.name}</div>
            {rows.map((row, i) => (
                <div key={i} className='bov-kv'>
                    <div className='bov-kv__label'>{row.label}</div>
                    <div className={`bov-kv__value${i === 0 && modifier === 'summary' ? ' bov-kv__value--accent' : ''}`}>
                        {blankAsDash(row.value)}
                    </div>
                </div>
            ))}
        </div>
    );
}

function OperatingData({table}) {
    const all = table.rows || [];
    const rows = all.slice(0, SCHEDULE_ROWS_MAX);
    const dropped = all.length - rows.length;
    const {showCommencement} = table;

    // The template's own schedule numbers its years "1", "2", "3", and the column is sized
    // for that. Agents also label option periods in this column - "Option 1: 11-15" - which
    // needs roughly half as much again. The wider column is only used when something in the
    // schedule actually calls for it, so an ordinary schedule keeps the template's spacing.
    const wideYears = rows.some(row => isLongYearLabel(row.valueCells));

    return (
        <div className='bov-card bov-card--aod'>
            <div className='bov-card__title'>{table.name}</div>

            <div className={`bov-aod${wideYears ? ' bov-aod--wideYears' : ''}${showCommencement ? '' : ' bov-aod--noWhen'}`}>
                <div className='bov-aod__row bov-aod__row--head'>
                    <div className='bov-aod__year'>{table.header[0]}</div>
                    {showCommencement
                        ? <div className='bov-aod__when'>{table.header[1]}</div>
                        : null}
                    <div className='bov-aod__annual'>{table.header[2]}</div>
                    <div className='bov-aod__increase'>{table.header[3]}</div>
                </div>

                {rows.map((row, i) => {
                    const cells = row.valueCells || [];
                    return (
                        <div key={i} className='bov-aod__row'>
                            <div className='bov-aod__year'>{cellValue(cells[0])}</div>
                            {showCommencement
                                ? <div className='bov-aod__when'>{cellValue(cells[1])}</div>
                                : null}
                            <div className='bov-aod__annual'>{cellValue(cells[2])}</div>
                            <div className='bov-aod__increase'>{cellValue(cells[3])}</div>
                        </div>
                    );
                })}

                {dropped > 0 ? (
                    <div className='bov-aod__row bov-aod__row--more'>
                        {`+${dropped} further ${dropped === 1 ? 'year' : 'years'} in the rent schedule`}
                    </div>
                ) : null}

                {/*
                  * The same cells as a schedule row, so the total lands under ANNUAL with the
                  * rents it totals rather than out at the right edge. Reusing the column
                  * classes keeps it aligned automatically in the wide-year layout and when the
                  * commencement column is dropped, instead of needing its own widths. The
                  * label is wider than the year column and overflows into the empty
                  * commencement cell beside it, which is what the template shows.
                  */}
                <div className='bov-aod__row bov-aod__row--total'>
                    <div className='bov-aod__year'>Net Operating Income</div>
                    {showCommencement ? <div className='bov-aod__when'/> : null}
                    <div className='bov-aod__annual'>{table.netOperatingIncome || '-'}</div>
                    <div className='bov-aod__increase'/>
                </div>
            </div>
        </div>
    );
}

/**
 * Whether a row's year label needs the wide column. "1-5" and "10" do not; anything with a
 * word in it - an option period - does.
 */
const LONG_YEAR_LABEL = /[a-z]/i;

function isLongYearLabel(cells) {
    return LONG_YEAR_LABEL.test(String((cells && cells[0] && cells[0].value) || ''));
}

/**
 * The reference prints a value against every label. A vacant property leaves most of the
 * lease blank, so an empty value shows as a dash rather than as an empty row - the row is
 * still there, which keeps the card's height and the page layout fixed.
 */
function blankAsDash(value) {
    return String(value == null ? '' : value).trim() === '' ? '-' : value;
}

/** Cells the builder could not compute come back undefined; the template prints a dash. */
function cellValue(cell) {
    const value = cell ? cell.value : '';
    return value === '' || value == null ? '-' : value;
}

function AgentLine({agents, index}) {
    const agent = (agents || [])[index];
    if (!agent) return <div className='bov-agent'/>;
    return (
        <div className='bov-agent'>
            {[agent.name, formatPhoneNumber(agent.phone)].filter(Boolean).join(' ')}
        </div>
    );
}

function firstWord(name) {
    if (!name) return '';
    const parts = String(name).trim().split(/\s+/);
    return parts.length > 1 ? `${parts[0]} ` : '';
}

function restOfName(name) {
    if (!name) return '';
    const parts = String(name).trim().split(/\s+/);
    const rest = parts.length > 1 ? parts.slice(1).join(' ') : parts[0];
    return rest ? `${rest}.` : '';
}
